<div class="pm-loading" *ngIf="loading" aria-busy="true">
  <div class="pm-loading-stage"></div>
  <div class="pm-loading-text">
    <div class="pm-loading-bar" style="width: 62%; height: 30px;"></div>
    <div class="pm-loading-bar" style="width: 88%;"></div>
    <div class="pm-loading-bar" style="width: 74%;"></div>
  </div>
</div>

<div class="pm-state" *ngIf="!loading && notFound">
  <h1 class="pm-state-title">{{ 'promotion.notFoundTitle' | translate }}</h1>
  <p class="pm-state-text">{{ 'promotion.notFoundBody' | translate }}</p>
  <button type="button" class="pm-primary-btn" [routerLink]="['/menu']">
    {{ 'promotion.backToMenu' | translate }}
  </button>
</div>

<article class="pm-detail" *ngIf="!loading && promotion">

  <!-- The stage. Media fills it entirely; with no media it becomes an accent-tinted field and the
       text stands on the surface instead of on a scrim. -->
  <!-- Tap-left / tap-right to move, press and hold to freeze — the reel's gestures. Bound here
       rather than on a layer of its own so the drag still reaches the Swiper track underneath. -->
  <div class="pm-stage" [class.pm-stage-empty]="!hasMedia"
       (pointerdown)="onStagePointerDown($event)"
       (pointerup)="onStagePointerUp($event)"
       (pointercancel)="onStagePointerCancel()">

    <ng-container *ngIf="hasMedia">
      <!-- The active item's own still, blown up and blurred. It is what a letterboxed slide rests
           on, so nothing ever sits on flat bars. -->
      <div class="pm-plate" *ngIf="backdropUrl as backdrop"
           [style.background-image]="'url(' + backdrop + ')'"></div>

      <swiper-container #track *ngIf="isGallery" class="pm-track"
        slides-per-view="1" space-between="0" pagination="false"
        (swiperslidechange)="onSlideChange($event)">
        <swiper-slide *ngFor="let item of mediaItems; let i = index; trackBy: trackByIndex">
          <ng-container [ngTemplateOutlet]="slide"
                        [ngTemplateOutletContext]="{ $implicit: item, index: i }">
          </ng-container>
        </swiper-slide>
      </swiper-container>

      <div class="pm-single" *ngIf="!isGallery">
        <ng-container [ngTemplateOutlet]="slide"
                      [ngTemplateOutletContext]="{ $implicit: mediaItems[0], index: 0 }">
        </ng-container>
      </div>

      <!-- Fixed neutral, never accent-tinted: legibility must not depend on the venue's hue or on
           the photograph underneath it. -->
      <div class="pm-scrim" aria-hidden="true"></div>
    </ng-container>

    <!-- Position at a glance, and a jump target each. Replaces dots, which stop being readable
         past four or five items. -->
    <div class="pm-bars" #bars *ngIf="isGallery" [class.pm-bars-paused]="storyHeld">
      <button type="button" class="pm-bar" *ngFor="let item of mediaItems; let i = index; trackBy: trackByIndex"
              [class.pm-bar-done]="i < activeIndex"
              [class.pm-bar-on]="i === activeIndex && !isTimedSlide && !isLiveSlide"
              (click)="goTo(i)"
              [attr.aria-label]="('promotion.goToItem' | translate) + ' ' + (i + 1)"
              [attr.aria-current]="i === activeIndex ? 'true' : null">
        <!-- A still's bar is its clock: a CSS animation over the same duration that advances the
             slide. Created fresh each time this becomes the active one — which is what restarts
             the animation. Toggling a class on a bar that had already finished would leave it
             sitting at full. -->
        <span class="pm-bar-fill" *ngIf="i === activeIndex && isTimedSlide"
              [style.animation-duration.ms]="stillDurationMs"></span>

        <!-- A clip's bar is the clip's own playhead, written to `--pm-progress` on the strip above
             frame by frame. No animation to keep in step, so a stall in the video stalls the bar
             with it instead of promising a slide change that has not happened. -->
        <span class="pm-bar-fill pm-bar-fill-live" *ngIf="i === activeIndex && isLiveSlide"></span>
      </button>
    </div>

    <div class="pm-top">
      <!-- WEB-ONLY, and the one control iOS does not have. There it is a sheet the diner swipes
           down to dismiss, and its own close button is dead code marked "Not Used". A route has no
           swipe-to-dismiss and browser Back is invisible, so without this a promotion with no
           authored button label — which is both of the live ones — would have no way out. -->
      <button type="button" class="pm-glass pm-icon-btn" (click)="back()"
              [attr.aria-label]="'promotion.back' | translate">
        <app-event-icon name="arrow-back" [size]="20"></app-event-icon>
      </button>

      <div class="pm-shop pm-glass" *ngIf="shopName">
        <app-event-image class="pm-shop-logo" *ngIf="shopLogoUrl"
                         [url]="shopLogoUrl" [alt]="shopName"></app-event-image>
        <span class="pm-shop-name">{{ shopName }}</span>
      </div>
    </div>

    <!-- Sound and crop. Both only exist while a clip is actually playing: under reduced motion the
         poster is standing in, and there is nothing to unmute or re-crop. -->
    <div class="pm-pills" *ngIf="activeIsPlayingVideo">
      <button type="button" class="pm-glass pm-pill" [class.pm-pill-on]="soundOn" (click)="toggleSound()">
        <app-event-icon [name]="soundOn ? 'volume-on' : 'volume-off'" [size]="16"></app-event-icon>
        <span>{{ (soundOn ? 'promotion.soundOn' : 'promotion.soundOff') | translate }}</span>
      </button>

      <button type="button" class="pm-glass pm-pill" *ngIf="canToggleFill"
              [class.pm-pill-on]="fillLandscape" (click)="toggleFill()">
        <app-event-icon [name]="fillLandscape ? 'aspect-fill' : 'aspect-fit'" [size]="16"></app-event-icon>
        <span>{{ (fillLandscape ? 'promotion.fillFrame' : 'promotion.fitFrame') | translate }}</span>
      </button>
    </div>

    <!-- Collapsed the copy is clamped and the media is still the point; open, it becomes a
         scrollable panel over the same full-bleed media. Nothing is ever lost, only folded. -->
    <div class="pm-text" [class.pm-text-clear]="!hasFooter"
         [class.pm-text-open]="textExpanded"
         [style.max-height]="textExpanded ? readingPanelMaxHeight : null">
      <h1 class="pm-title" [appCompactPast]="2" *ngIf="title"
          [class.pm-clamp-title]="!textExpanded"
          (appOverflows)="onTextOverflow($event)">{{ title }}</h1>
      <p class="pm-body" *ngIf="description"
         [class.pm-clamp-body]="!textExpanded"
         (appOverflows)="onTextOverflow($event)">{{ description }}</p>

      <!-- Offered only when it leads somewhere: `textTruncated` is measured, never guessed from
           the string length. -->
      <button type="button" class="pm-more" *ngIf="!textExpanded && textTruncated"
              (click)="expandText()">{{ 'events.readMore' | translate }}</button>

      <button type="button" class="pm-more pm-more-close" *ngIf="textExpanded"
              (click)="collapseText()"
              [attr.aria-label]="'promotion.close' | translate">
        <app-event-icon name="arrow-back" [size]="16"></app-event-icon>
        <span>{{ 'promotion.close' | translate }}</span>
      </button>
    </div>
  </div>

  <!-- Only when the admin authored a label. No fallback: see `actionLabel`. -->
  <div class="pm-footer" *ngIf="hasFooter">
    <button type="button" class="pm-action-btn" (click)="onAction()">{{ actionLabel }}</button>
  </div>
</article>

<!-- One slide. The media element is the same `app-event-media` the events feature renders its hero
     with; everything around it is this screen's. -->
<ng-template #slide let-item let-index="index">
  <app-event-media class="pm-media" [item]="item" [alt]="title"
                   [fit]="fitFor(index)"
                   [muted]="mutedFor(index)"
                   [loop]="!isGallery"
                   [paused]="storyHeld"
                   (ended)="onMediaEnded(index)"
                   (failed)="onMediaFailed(index)"
                   (aspectResolved)="onAspectResolved(index, $event)"></app-event-media>
</ng-template>
